import { useMutation } from "@tanstack/react-query"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import Modal from "@/globalComponents/molecules/Modal"
import TextField from "@/globalComponents/molecules/TextField"
import { ExpectedError, ParkPlateUpdateError } from "@/utilities/expectedErrors"
import { licensePlateValidationRule, requiredRule } from "@/utilities/form"
import { normalizeLicensePlate } from "@/utilities/normalizers"

interface PlateUpdateModalProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  receiptID: string
  currentPlate: string
  onSuccess?: () => void
}

interface PlateUpdateFormSchema {
  plate: string
}

function PlateUpdateModal(props: PlateUpdateModalProps) {
  const { t } = useTranslation(["receipt", "errors"])
  const form = useForm<PlateUpdateFormSchema>({
    defaultValues: {
      plate: "",
    },
    mode: "onBlur",
  })
  const { errors } = form.formState

  const { mutate, isPending, reset } = useMutation({
    mutationFn,
    onSuccess,
    onError,
  })

  return (
    <Modal {...props} onOpenChange={handleOpenChange}>
      <Modal.Header>
        <Heading level={2} slot="title">
          {t("plateUpdateModal.header")}
        </Heading>
      </Modal.Header>
      <Modal.Body>
        <Text>{t("plateUpdateModal.description")}</Text>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <fieldset disabled={isPending}>
            {errors?.root && <FieldError>{errors.root.message}</FieldError>}
            <Controller<PlateUpdateFormSchema, "plate">
              name="plate"
              control={form.control}
              rules={{
                required: requiredRule(t("common.required", { ns: "errors" })),
                validate: licensePlateValidationRule(t),
              }}
              render={({
                field: { ref, onChange, ...rest },
                fieldState: { error, invalid },
              }) => (
                <TextField
                  {...rest}
                  onChange={(v) => onChange(normalizeLicensePlate(v))}
                  isInvalid={invalid}
                >
                  <Label>
                    {t("plateUpdateModal.plateFieldLabel")}
                  </Label>
                  <TextField.Input
                    innerRef={ref}
                    placeholder={props.currentPlate}
                    maxLength={18}
                    style={{ textTransform: "uppercase" }}
                  />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
            <Button type="submit" buttonType="primary" isPending={isPending}>
              {t("plateUpdateModal.submit")}
            </Button>
          </fieldset>
        </form>
      </Modal.Body>
    </Modal>
  )

  function onSubmit(data: PlateUpdateFormSchema) {
    mutate(data)
  }

  async function mutationFn(data: PlateUpdateFormSchema) {
    const response = await api.park.plateUpdate.POST({
      receiptID: props.receiptID,
      newPlate: data.plate,
    })

    if ("failReason" in response) {
      throw new ParkPlateUpdateError(response.failReason)
    }

    return response
  }

  function onSuccess() {
    handleOpenChange(false)
    if (props.onSuccess) {
      props.onSuccess()
    }
  }

  function onError(error: unknown) {
    if (error instanceof ExpectedError) {
      form.setError("root", {
        message: t(error.translationKey, { ns: "errors" }),
      })
    } else if (error instanceof Error) {
      form.setError("root", {
        message: error.message,
      })
    } else {
      form.setError("root", {
        message: t("common.somethingWentWrong", { ns: "errors" }),
      })
    }
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      reset()
      form.reset()
      form.clearErrors()
    }
    props.onOpenChange(isOpen)
  }
}

export default PlateUpdateModal
